import { useMutation } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { markEventAsRead } from "../api/markEventAsRead";
import type { WebSocketMessage } from "../types";

interface MessageBubbleProps {
  message: WebSocketMessage;
  memberEmail: string;
  sourceEntity: string;
}

export function MessageBubble({ message, memberEmail, sourceEntity }: MessageBubbleProps) {
  // Determine role from member role: "agent" -> "assistant", "client" -> "user"
  const role = message.member?.role === "agent" ? "assistant" : "user";

  const unreadEventsByUsername = useSelector(
    (state: { ChatReducer: { unreadEventsByUsername: Set<string> } }) =>
      state.ChatReducer.unreadEventsByUsername,
  );

  const dispatch = useDispatch();
  const deleteUnreadEventByUsername = useCallback(
    (evtUid: string, roomUid: string) => {
      dispatch({ type: "DELETE_UNREAD_EVENT_BY_USERNAME", eventId: evtUid, roomUid });
    },
    [dispatch],
  );

  const markEventAsReadMutation = useMutation({
    mutationFn: ({
      eventId,
      sourceEntity,
      username,
    }: {
      eventId: string;
      sourceEntity: string;
      username: string;
    }) => markEventAsRead({ sourceEntity, username, eventId }),
  });

  useEffect(() => {
    if (!message.uid) return;
    if (!sourceEntity) return;
    if (!memberEmail) return;

    if (unreadEventsByUsername.has(message.uid)) {
      markEventAsReadMutation.mutate({
        eventId: message.uid,
        sourceEntity,
        username: memberEmail,
      });
      deleteUnreadEventByUsername(message.uid, message.room_uid);
    }
  }, [
    message.uid,
    markEventAsReadMutation.mutate,
    sourceEntity,
    memberEmail,
    unreadEventsByUsername,
    deleteUnreadEventByUsername,
  ]);

  return (
    <div
      className={`MessageBubble grid ${role === "user" ? "justify-items-end" : "justify-items-start"}`}
    >
      {message.member?.email && (
        <p className={`text-xs mb-1 px-1 ${role === "user" ? "text-gray-600" : "text-gray-600"}`}>
          {message.member.email}
        </p>
      )}
      <div
        className={`min-w-[15rem] max-w-[80%] rounded-lg px-4 py-2 ${
          role === "user" ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-900"
        }`}
      >
        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
      </div>
      <p className={`text-xs mt-1 px-1 ${role === "user" ? "text-gray-600" : "text-gray-600"}`}>
        {DateTime.fromMillis(message.timestamp).toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
      </p>
    </div>
  );
}
